import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import CookieBanner from '../../components/feature/CookieBanner';
import { Link } from 'react-router-dom';
import { useSEO, getBreadcrumbSchema } from '../../utils/seo';

const ContactoPage = () => {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    tipoInmueble: '',
    municipio: '',
    urgencia: 'normal',
    mensaje: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useSEO({
    title: 'Contacto - Limpieza Post Mortem Barcelona | BIOTRAUMA',
    description: 'Contacte con BIOTRAUMA POST MORTEM para solicitar información o presupuesto sin compromiso. Disponibles 24/7 en Barcelona. Teléfono: 629 501 941.',
    keywords: 'contacto, presupuesto, valoración, limpieza post mortem, Barcelona, teléfono',
    canonical: '/contacto',
    schema: getBreadcrumbSchema([
      { name: 'Inicio', url: '/' },
      { name: 'Contacto', url: '/contacto' }
    ])
  });

  useEffect(() => {
    document.title = 'Contacto - Limpieza Post Mortem Barcelona | BIOTRAUMA';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Contacte con BIOTRAUMA POST MORTEM para limpieza post mortem en Barcelona. Valoración gratuita sin compromiso. Disponibles 24/7. Tel: +34 629 501 941');
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formElement = e.target as HTMLFormElement;
      const formDataToSend = new URLSearchParams();
      
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d52gl5rontabvievtiqg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          nombre: '',
          email: '',
          telefono: '',
          tipoInmueble: '',
          municipio: '',
          urgencia: 'normal',
          mensaje: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const municipios = [
    'Barcelona', 'L\'Hospitalet de Llobregat', 'Badalona', 'Santa Coloma de Gramenet',
    'Sant Adrià de Besòs', 'Cornellà de Llobregat', 'El Prat de Llobregat', 'Sant Boi de Llobregat',
    'Castelldefels', 'Gavà', 'Viladecans', 'Sant Cugat del Vallès', 'Cerdanyola del Vallès',
    'Rubí', 'Terrassa', 'Sabadell', 'Mataró', 'Granollers', 'Mollet del Vallès', 'Martorell', 'Otro'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-24 bg-gradient-to-b from-light to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center space-x-2 bg-primary/10 rounded-full px-4 py-2 mb-6"
          >
            <span className="text-primary font-semibold text-sm">Contacto</span>
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-display font-bold text-5xl lg:text-6xl text-dark mb-6 leading-tight"
          >
            Contacte con nosotros
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed"
          >
            Disponibles 24 horas, 7 días a la semana. Valoración gratuita y sin compromiso para servicios de <strong>limpieza post mortem en Barcelona</strong>. <Link to="/servicios" className="text-primary hover:underline cursor-pointer">Conozca todos nuestros servicios especializados</Link>.
          </motion.p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-gradient-to-br from-primary to-primary/80 rounded-3xl p-10 lg:p-12 text-white"
            >
              <h2 className="font-display font-bold text-3xl mb-8">
                Información de Contacto
              </h2>
              
              <div className="space-y-8 mb-12">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Dirección</h3>
                    <p className="text-white/90 leading-relaxed">
                      Plaça de Julio González, 8<br />
                      Sant Martí, 08005 Barcelona
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Teléfono</h3>
                    <a href="tel:+34629501941" className="text-white/90 hover:text-white transition-colors text-lg cursor-pointer">
                      +34 629 501 941
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Email</h3>
                    <a href="mailto:info@postmorten.biotrauma.es" className="text-white/90 hover:text-white transition-colors break-all cursor-pointer">
                      info@postmorten.biotrauma.es
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Horario</h3>
                    <p className="text-white/90">
                      Disponibles 24 horas<br />
                      7 días a la semana
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-t border-white/20 pt-8">
                <h3 className="font-semibold text-lg mb-4">Contacto Rápido</h3>
                <div className="flex flex-col sm:flex-row gap-4">
                  <a
                    href="https://wa.me/34629501941?text=Hola,%20necesito%20información"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center px-6 py-3 bg-white text-primary font-semibold rounded-full hover:bg-white/90 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-whatsapp-line text-xl mr-2"></i>
                    WhatsApp
                  </a>
                  <a
                    href="tel:+34629501941"
                    className="flex items-center justify-center px-6 py-3 bg-white/20 text-white font-semibold rounded-full hover:bg-white/30 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-phone-line text-xl mr-2"></i>
                    Llamar
                  </a>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="bg-white border border-gray-200 rounded-3xl p-8 lg:p-10">
                <h2 className="font-display font-bold text-3xl text-dark mb-2">
                  Solicitar Valoración
                </h2>
                <p className="text-gray-600 mb-8">
                  Complete el formulario y nos pondremos en contacto con usted lo antes posible
                </p>

                <form onSubmit={handleSubmit} data-readdy-form id="contacto-form">
                  <div className="space-y-6">
                    <div>
                      <label htmlFor="nombre" className="block text-sm font-semibold text-dark mb-2">
                        Nombre completo *
                      </label>
                      <input
                        type="text"
                        id="nombre"
                        name="nombre"
                        value={formData.nombre}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-primary transition-colors"
                        placeholder="Su nombre"
                      />
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="email" className="block text-sm font-semibold text-dark mb-2">
                          Email *
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-primary transition-colors"
                          placeholder="su@email.com"
                        />
                      </div>

                      <div>
                        <label htmlFor="telefono" className="block text-sm font-semibold text-dark mb-2">
                          Teléfono *
                        </label>
                        <input
                          type="tel"
                          id="telefono"
                          name="telefono"
                          value={formData.telefono}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-primary transition-colors"
                          placeholder="+34 600 000 000"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="tipoInmueble" className="block text-sm font-semibold text-dark mb-2">
                          Tipo de inmueble *
                        </label>
                        <select
                          id="tipoInmueble"
                          name="tipoInmueble"
                          value={formData.tipoInmueble}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-primary transition-colors cursor-pointer"
                        >
                          <option value="">Seleccione...</option>
                          <option value="piso">Piso</option>
                          <option value="casa">Casa</option>
                          <option value="local">Local</option>
                          <option value="oficina">Oficina</option>
                          <option value="otro">Otro</option>
                        </select>
                      </div>

                      <div>
                        <label htmlFor="municipio" className="block text-sm font-semibold text-dark mb-2">
                          Municipio *
                        </label>
                        <select
                          id="municipio"
                          name="municipio"
                          value={formData.municipio}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-primary transition-colors cursor-pointer"
                        >
                          <option value="">Seleccione...</option>
                          {municipios.map((muni) => (
                            <option key={muni} value={muni}>{muni}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-dark mb-3">
                        Nivel de urgencia *
                      </label>
                      <div className="grid grid-cols-3 gap-4">
                        <label className="relative cursor-pointer">
                          <input
                            type="radio"
                            name="urgencia"
                            value="normal"
                            checked={formData.urgencia === 'normal'}
                            onChange={handleChange}
                            className="sr-only"
                          />
                          <div className={`px-4 py-3 border-2 rounded-xl text-center font-medium transition-all ${
                            formData.urgencia === 'normal'
                              ? 'border-primary bg-primary/5 text-primary'
                              : 'border-gray-200 text-gray-600 hover:border-gray-300'
                          }`}>
                            Normal
                          </div>
                        </label>
                        <label className="relative cursor-pointer">
                          <input
                            type="radio"
                            name="urgencia"
                            value="media"
                            checked={formData.urgencia === 'media'}
                            onChange={handleChange}
                            className="sr-only"
                          />
                          <div className={`px-4 py-3 border-2 rounded-xl text-center font-medium transition-all ${
                            formData.urgencia === 'media'
                              ? 'border-primary bg-primary/5 text-primary'
                              : 'border-gray-200 text-gray-600 hover:border-gray-300'
                          }`}>
                            Media
                          </div>
                        </label>
                        <label className="relative cursor-pointer">
                          <input
                            type="radio"
                            name="urgencia"
                            value="alta"
                            checked={formData.urgencia === 'alta'}
                            onChange={handleChange}
                            className="sr-only"
                          />
                          <div className={`px-4 py-3 border-2 rounded-xl text-center font-medium transition-all ${
                            formData.urgencia === 'alta'
                              ? 'border-primary bg-primary/5 text-primary'
                              : 'border-gray-200 text-gray-600 hover:border-gray-300'
                          }`}>
                            Alta
                          </div>
                        </label>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="mensaje" className="block text-sm font-semibold text-dark mb-2">
                        Descripción de la situación *
                      </label>
                      <textarea
                        id="mensaje"
                        name="mensaje"
                        value={formData.mensaje}
                        onChange={handleChange}
                        required
                        maxLength={500}
                        rows={5}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-primary transition-colors resize-none"
                        placeholder="Describa brevemente la situación y el servicio que necesita..."
                      ></textarea>
                      <div className="text-right text-sm text-gray-500 mt-1">
                        {formData.mensaje.length}/500 caracteres
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full px-8 py-4 bg-primary text-white font-semibold rounded-full hover:bg-primary/90 transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer flex items-center justify-center"
                    >
                      {isSubmitting ? (
                        <>
                          <i className="ri-loader-4-line animate-spin text-xl mr-2"></i>
                          Enviando...
                        </>
                      ) : (
                        <>
                          Enviar Solicitud
                          <i className="ri-send-plane-line text-xl ml-2"></i>
                        </>
                      )}
                    </button>

                    {submitStatus === 'success' && (
                      <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-green-700 flex items-start space-x-3">
                        <i className="ri-checkbox-circle-line text-2xl flex-shrink-0"></i>
                        <div>
                          <strong className="block mb-1">¡Solicitud enviada con éxito!</strong>
                          <span className="text-sm">Nos pondremos en contacto con usted lo antes posible.</span>
                        </div>
                      </div>
                    )}

                    {submitStatus === 'error' && (
                      <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 flex items-start space-x-3">
                        <i className="ri-error-warning-line text-2xl flex-shrink-0"></i>
                        <div>
                          <strong className="block mb-1">Error al enviar</strong>
                          <span className="text-sm">Por favor, inténtelo de nuevo o contáctenos por teléfono.</span>
                        </div>
                      </div>
                    )}
                  </div>
                </form>

                <div className="mt-8 pt-8 border-t border-gray-200">
                  <p className="text-sm text-gray-500 text-center">
                    Al enviar este formulario, acepta nuestra <Link to="/privacidad" className="text-primary hover:underline cursor-pointer">política de privacidad</Link> y el tratamiento de sus datos personales.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-light">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-display font-bold text-4xl lg:text-5xl text-dark mb-6">
              Formas de contacto
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Servicio de <strong>limpieza post mortem</strong> en Barcelona y toda la provincia. <Link to="/empresa" className="text-primary hover:underline cursor-pointer">Conozca nuestra trayectoria y valores</Link> o <Link to="/faq" className="text-primary hover:underline cursor-pointer">consulte nuestras preguntas frecuentes</Link>.
            </p>
          </div>

          <div className="bg-white rounded-3xl overflow-hidden shadow-xl">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2993.4982!2d2.1743!3d41.4037!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x12a4a2f8b8b8b8b8%3A0x8b8b8b8b8b8b8b8b!2sPla%C3%A7a%20de%20Julio%20Gonz%C3%A1lez%2C%208%2C%2008005%20Barcelona!5e0!3m2!1ses!2ses!4v1234567890"
              width="100%"
              height="500"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Ubicación BIOTRAUMA POST MORTEM"
            ></iframe>
          </div>

          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {['Barcelona Capital', 'Área Metropolitana', 'Vallès', 'Maresme'].map((area, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 text-center shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-map-pin-line text-2xl text-primary"></i>
                </div>
                <h3 className="font-semibold text-dark mb-2">{area}</h3>
                <p className="text-sm text-gray-600">Cobertura completa</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
      <CookieBanner />
    </div>
  );
};

export default ContactoPage;
